import React, { useState, useEffect, useMemo, useCallback } from 'react';
import HtmlEditor from '../../HtmlEditor';
import { getThumbnailUrl } from '../../../../utils/imageUtils';
import ImagePickerModal from '../../shared/ImagePickerModal';
import HotelCacheManager from '../../HotelCacheManager';
import HotelVideoManager from '../../hotels/HotelVideoManager';
import HotelPageBuilder from '../../hotels/HotelPageBuilder';
import HotelEnrichment from '../../../hotels/HotelEnrichment';
import { RoomMappingPanel } from '../../RoomMappingPanel';

interface HotelDetailEditorProps {
    hotelId: string;
    onClose: () => void;
    onSave?: () => void;
}

interface HotelData {
    _id: string;
    tourvisioId: string;
    name: string;
    stars: number;
    description?: string;
    location?: {
        country?: string;
        city?: string;
        address?: string;
        coordinates?: {
            latitude?: number;
            longitude?: number;
        };
    };
    contact?: {
        phone?: string;
        fax?: string;
        email?: string;
        website?: string;
    };
    instagramUrl?: string;
    webUrl?: string;
    images?: Array<{
        url: string;
        thumbnailUrl?: string;
        isMain?: boolean;
        source?: string;
        isApproved?: boolean;
    }>;
    roomTypes?: Array<{
        id: string;
        name: string;
        description?: string;
    }>;
    boardTypes?: Array<{
        code: string;
        name: string;
    }>;
    facilities?: Array<{
        id: string;
        name: string;
        category?: string;
        isPaid?: boolean;
    }>;
    themes?: Array<{
        id: string;
        name: string;
    }>;
    concepts?: Array<{
        id: string;
        name: string;
        description?: string;
    }>;
    tourvisioTexts?: Array<{
        code: string;
        name: string;
        text: string;
        textType?: number;
    }>;
    facilityCategories?: Array<{
        name: string;
        facilities: Array<{
            id: string;
            name: string;
            note?: string;
            isPriced?: boolean;
        }>;
    }>;
    cachedPrices?: Array<{
        date: string;
        minPrice: number;
        currency: string;
        roomType?: string;
        boardType?: string;
    }>;
    priceRange?: {
        min: number;
        max: number;
        currency: string;
    };
    isActive?: boolean;
    isFeatured?: boolean;
    lockContent?: boolean;
    lastApiSync?: string;
    apiSyncSource?: string;
    bookingReviews?: {
        score?: number;
        reviewCount?: number;
        summary?: { en?: string; sv?: string };
        reviews?: Array<{
            author?: string;
            rating?: number;
            text?: { en?: string; sv?: string };
            date?: string;
            source?: string;
            isVisible?: boolean;
        }>;
        pros?: Array<{ en?: string; sv?: string }>;
        guestMood?: { en?: string; sv?: string };
        lastFetched?: string;
        lastManualEdit?: string;
    };
    adminDescription?: {
        en?: string;
        sv?: string;
        da?: string;
        generatedAt?: string;
        generatedBy?: string;
    };
    adminShortDescription?: {
        en?: string;
        sv?: string;
        da?: string;
        generatedAt?: string;
        generatedBy?: string;
    };
    distanceToBeach?: number;
    distanceToCenter?: number;
    giataId?: string;
}

interface CachedData {
    hotel: {
        priceRange?: { min: number; max: number; currency: string };
        roomTypes?: Array<{ id: string; name: string }>;
        boardTypes?: Array<{ code: string; name: string }>;
        cachedPricesCount?: number;
    };
    recentCachedOffers?: Array<{
        searchParams: any;
        cachedAt: string;
        offers: any[];
    }>;
}

type TabType = 'details' | 'features' | 'gallery' | 'rooms' | 'reviews' | 'cached' | 'media' | 'page_builder' | 'enriched' | 'api';

const API_BASE = '/api/hotels';

export const HotelDetailEditor: React.FC<HotelDetailEditorProps> = ({ hotelId, onClose, onSave }) => {
    const [activeTab, setActiveTab] = useState<TabType>('details');
    const [hotel, setHotel] = useState<HotelData | null>(null);
    const [cachedData, setCachedData] = useState<CachedData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [uploading, setUploading] = useState(false);
    const [imageUrlInput, setImageUrlInput] = useState('');
    const [hotelImagePickerOpen, setHotelImagePickerOpen] = useState(false);

    // Google Photos Management
    const [googleImages, setGoogleImages] = useState<Array<{ url: string; thumbnail: string }>>([]);
    const [showAllGoogleImages, setShowAllGoogleImages] = useState(false);
    const [loadingGoogleImages, setLoadingGoogleImages] = useState(false);
    const [approvedGoogleImages, setApprovedGoogleImages] = useState(false);
    const GOOGLE_PHOTOS_INITIAL_LIMIT = 10;

    // AI & Translation state
    const [translating, setTranslating] = useState<string | null>(null);
    const [generatingDesc, setGeneratingDesc] = useState(false);
    const [fetchingReviews, setFetchingReviews] = useState(false);
    const [syncingTourvisio, setSyncingTourvisio] = useState(false);
    const [syncResult, setSyncResult] = useState<any>(null);

    // Fetch hotel data
    const fetchHotel = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/${hotelId}`);
            const data = await res.json();
            if (data.success) {
                setHotel(data.hotel);
            }
        } catch (error) {
            console.error('Fetch hotel error:', error);
            setMessage({ type: 'error', text: 'Failed to load hotel' });
        } finally {
            setLoading(false);
        }
    }, [hotelId]);

    // Fetch cached data
    const fetchCachedData = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/${hotelId}/cached-data`);
            const data = await res.json();
            if (data.success) {
                setCachedData(data);
            }
        } catch (error) {
            console.error('Fetch cached data error:', error);
        }
    }, [hotelId]);

    useEffect(() => {
        fetchHotel();
        fetchCachedData();
    }, [fetchHotel, fetchCachedData]);

    // Save hotel changes
    const handleSave = async () => {
        if (!hotel) return;
        try {
            setSaving(true);
            const res = await fetch(`${API_BASE}/${hotelId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(hotel)
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Hotel saved successfully' });
                onSave?.();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to save' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Save failed' });
        } finally {
            setSaving(false);
        }
    };

    // Handle hotel deletion
    const handleDelete = async () => {
        if (!hotel) return;

        const confirmed = window.confirm(
            `Are you sure you want to delete "${hotel.name}"?\n\nThis action cannot be undone!`
        );

        if (!confirmed) return;

        try {
            setDeleting(true);
            const res = await fetch(`${API_BASE}/${hotelId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await res.json();

            if (data.success) {
                setMessage({ type: 'success', text: 'Hotel deleted successfully' });
                // Close editor after short delay to show success message
                setTimeout(() => {
                    onSave?.(); // Trigger refresh of hotel list
                    onClose();
                }, 1000);
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to delete hotel' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Delete failed - network error' });
        } finally {
            setDeleting(false);
        }
    };

    // Handle image upload
    const handleImageUpload = async (files: FileList) => {
        if (files.length === 0) return;

        try {
            setUploading(true);
            const formData = new FormData();
            Array.from(files).forEach(file => formData.append('images', file));

            const res = await fetch(`${API_BASE}/${hotelId}/images/upload`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();

            if (data.success) {
                setMessage({ type: 'success', text: `${data.images.length} images uploaded` });
                fetchHotel(); // Refresh hotel data
            } else {
                setMessage({ type: 'error', text: data.error || 'Upload failed' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Upload failed' });
        } finally {
            setUploading(false);
        }
    };

    // Sync cache to hotel
    const handleSyncCache = async () => {
        try {
            setSaving(true);
            const res = await fetch(`${API_BASE}/${hotelId}/sync-cache`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: data.message });
                fetchHotel();
                fetchCachedData();
            } else {
                setMessage({ type: 'error', text: data.error });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Sync failed' });
        } finally {
            setSaving(false);
        }
    };

    // Fetch images from API
    const handleFetchApiImages = async () => {
        try {
            setSaving(true);
            const res = await fetch(`${API_BASE}/${hotelId}/images`);
            const data = await res.json();
            if (data.success && data.images?.length > 0) {
                // Import images
                const importRes = await fetch(`${API_BASE}/${hotelId}/import-images`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ images: data.images, downloadToServer: true })
                });
                const importData = await importRes.json();
                if (importData.success) {
                    setMessage({ type: 'success', text: `${data.images.length} images imported from API` });
                    fetchHotel();
                }
            } else {
                setMessage({ type: 'error', text: 'No images found from API' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to fetch API images' });
        } finally {
            setSaving(false);
        }
    };

    const handleAddImageUrl = () => {
        if (!imageUrlInput || !hotel) return;
        const newImage = {
            url: imageUrlInput,
            isMain: false,
            source: 'manual_url',
            isApproved: true
        };
        // Optimistic update
        setHotel({ ...hotel, images: [...(hotel.images || []), newImage] });
        setImageUrlInput('');
    };

    const handleSetMainImage = (index: number) => {
        if (!hotel || !hotel.images) return;
        const updatedImages = hotel.images.map((img, i) => ({
            ...img,
            isMain: i === index
        }));
        setHotel({ ...hotel, images: updatedImages });
    };

    const handleDeleteImage = (index: number) => {
        if (!hotel || !hotel.images) return;
        const updatedImages = hotel.images.filter((_, i) => i !== index);
        setHotel({ ...hotel, images: updatedImages });
    };

    // Fetch Google Photos for the hotel
    const handleFetchGoogleImages = async () => {
        if (!hotel) return;
        try {
            setLoadingGoogleImages(true);
            const res = await fetch(`/api/hotels/${hotelId}/google-photos`);
            const data = await res.json();
            if (data.success && data.images) {
                setGoogleImages(data.images.map((img: any) => ({
                    url: img.original || img.url,
                    thumbnail: img.thumbnail || img.url
                })));
                setMessage({ type: 'success', text: `Found ${data.images.length} Google photos` });
            } else {
                setMessage({ type: 'error', text: data.error || 'No Google photos found' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to fetch Google photos' });
        } finally {
            setLoadingGoogleImages(false);
        }
    };

    // Approve and copy selected Google images to hotel images
    const handleApproveGoogleImages = (selectedUrls: string[]) => {
        if (!hotel) return;
        const newImages = selectedUrls.map(url => ({
            url,
            isMain: false,
            source: 'google_approved',
            isApproved: true
        }));
        setHotel({
            ...hotel,
            images: [...(hotel.images || []), ...newImages],
        });
        // Also set the flag to allow Google images on public website
        setApprovedGoogleImages(true);
        setMessage({ type: 'success', text: `${selectedUrls.length} Google photos approved and added` });
    };

    // Toggle Google images approval for public website
    const handleToggleGoogleApproval = () => {
        setApprovedGoogleImages(!approvedGoogleImages);
        // This will be saved along with other hotel data
    };


    // Translation handler
    const handleTranslate = async (text: string, fromLang: string, toLang: string, targetField: string) => {
        if (!text || !hotel) return;
        setTranslating(targetField);
        try {
            const res = await fetch(`${API_BASE}/${hotelId}/translate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text, fromLang, toLang })
            });
            const data = await res.json();
            if (data.success && data.translated) {
                // Update the correct field
                if (targetField === 'adminDescription.sv') {
                    setHotel({ ...hotel, adminDescription: { ...hotel.adminDescription, sv: data.translated } });
                } else if (targetField === 'adminDescription.en') {
                    setHotel({ ...hotel, adminDescription: { ...hotel.adminDescription, en: data.translated } });
                } else if (targetField === 'adminDescription.da') {
                    setHotel({ ...hotel, adminDescription: { ...hotel.adminDescription, da: data.translated } });
                } else if (targetField === 'adminShortDescription.sv') {
                    setHotel({ ...hotel, adminShortDescription: { ...hotel.adminShortDescription, sv: data.translated } });
                } else if (targetField === 'adminShortDescription.en') {
                    setHotel({ ...hotel, adminShortDescription: { ...hotel.adminShortDescription, en: data.translated } });
                } else if (targetField === 'adminShortDescription.da') {
                    setHotel({ ...hotel, adminShortDescription: { ...hotel.adminShortDescription, da: data.translated } });
                }
                setMessage({ type: 'success', text: `Translated to ${toLang.toUpperCase()}` });
            } else {
                setMessage({ type: 'error', text: data.error || 'Translation failed' });
            }
        } catch {
            setMessage({ type: 'error', text: 'Translation failed' });
        } finally {
            setTranslating(null);
        }
    };

    // AI Description generation
    const handleGenerateDescription = async () => {
        if (!hotel) return;
        setGeneratingDesc(true);
        try {
            const res = await fetch(`${API_BASE}/${hotelId}/generate-description`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                setHotel({
                    ...hotel,
                    adminDescription: data.description,
                    adminShortDescription: data.shortDescription
                });
                setMessage({ type: 'success', text: 'AI descriptions generated!' });
            } else {
                setMessage({ type: 'error', text: data.error || 'Generation failed' });
            }
        } catch {
            setMessage({ type: 'error', text: 'Generation failed' });
        } finally {
            setGeneratingDesc(false);
        }
    };

    // Fetch reviews using AI
    const handleFetchReviews = async () => {
        setFetchingReviews(true);
        try {
            const res = await fetch(`${API_BASE}/${hotelId}/reviews/fetch`, { method: 'POST' });
            const data = await res.json();
            if (data.success && hotel) {
                setHotel({ ...hotel, bookingReviews: data.reviews });
                setMessage({ type: 'success', text: 'Reviews fetched successfully!' });
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to fetch reviews' });
            }
        } catch {
            setMessage({ type: 'error', text: 'Failed to fetch reviews' });
        } finally {
            setFetchingReviews(false);
        }
    };

    const tabs = [
        { id: 'details' as TabType, label: 'Details', icon: '📋' },
        { id: 'features' as TabType, label: 'Features', icon: '✨' },
        { id: 'gallery' as TabType, label: 'Gallery', icon: '🖼️' },
        { id: 'rooms' as TabType, label: 'Room Types', icon: '🛏️' },
        { id: 'reviews' as TabType, label: 'Reviews', icon: '⭐' },
        { id: 'cached' as TabType, label: 'Cached Data', icon: '💾' },
        { id: 'media' as TabType, label: 'Media', icon: '🎬' },
        { id: 'page_builder' as TabType, label: 'Page Builder', icon: '🏗️' },
        { id: 'enriched' as TabType, label: 'Enriched', icon: '🔮' },
        { id: 'api' as TabType, label: 'API Sync', icon: '🔄' }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full p-12">
                <div className="text-center">
                    <div className="animate-spin w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto" />
                    <p className="mt-4 text-gray-400">Loading hotel...</p>
                </div>
            </div>
        );
    }

    if (!hotel) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-12 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="text-center">
                    <div className="text-4xl mb-4">🏨</div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Hotel Not Found</h3>
                    <p className="text-gray-500 mb-6">The hotel record could not be loaded or no longer exists.</p>
                    <button 
                        onClick={onClose} 
                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    >
                        Return to List
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="bg-white dark:bg-gray-900 flex-1 flex flex-col min-h-0">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white flex-shrink-0" title="Back to list">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="min-w-0">
                            <h2 className="text-lg sm:text-xl font-bold text-white truncate">{hotel.name}</h2>
                            <p className="text-xs sm:text-sm text-white/70 truncate">
                                {'⭐'.repeat(hotel.stars || 0)} • {hotel.location?.city}, {hotel.location?.country}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-3 sm:px-5 py-1.5 sm:py-2 bg-white/20 hover:bg-white/30 text-white text-xs sm:text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                        >
                            {saving ? 'Saving...' : '💾 Save'}
                        </button>
                    </div>
                </div>

                {/* Message */}
                {message && (
                    <div className={`px-6 py-3 ${message.type === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>
                        {message.text}
                        <button onClick={() => setMessage(null)} className="float-right">×</button>
                    </div>
                )}

                {/* Tabs */}
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 overflow-x-auto">
                    <div className="flex gap-1 whitespace-nowrap min-w-0">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-3 sm:px-4 py-3 font-medium text-xs sm:text-sm border-b-2 transition-colors flex-shrink-0 ${activeTab === tab.id
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                            >
                                <span className="mr-1 sm:mr-2">{tab.icon}</span>
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 dark:text-gray-200">
                    {/* Details Tab */}
                    {activeTab === 'details' && (
                        <div className="space-y-6">
                            {/* Basic Info */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                                    <input
                                        type="text"
                                        value={hotel.name}
                                        onChange={e => setHotel({ ...hotel, name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Stars</label>
                                    <select
                                        value={hotel.stars}
                                        onChange={e => setHotel({ ...hotel, stars: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    >
                                        {[1, 2, 3, 4, 5].map(s => (
                                            <option key={s} value={s}>{s} Stars</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">GIATA ID</label>
                                    <input
                                        type="text"
                                        value={hotel.giataId || ''}
                                        onChange={e => setHotel({ ...hotel, giataId: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        placeholder="e.g. 554963"
                                    />
                                </div>
                            </div>

                            {/* API Description (read-only, from Tourvisio) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">API Description (Tourvisio)</label>
                                <textarea
                                    value={hotel.description || ''}
                                    onChange={e => setHotel({ ...hotel, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
                                />
                            </div>

                            {/* Admin Description (bilingual, takes priority) */}
                            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-5 space-y-4">
                                <div className="flex justify-between items-center">
                                    <h4 className="font-semibold text-indigo-800 dark:text-indigo-300 flex items-center gap-2">✍️ Admin Description <span className="text-xs font-normal text-indigo-500">(overrides API on site)</span></h4>
                                    <button
                                        onClick={handleGenerateDescription}
                                        disabled={generatingDesc}
                                        className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
                                    >
                                        {generatingDesc ? (
                                            <><svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Generating...</>
                                        ) : '🤖 Generate with AI'}
                                    </button>
                                </div>

                                {/* Long description EN/SV/DA */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">🇬🇧 English</label>
                                            <button
                                                onClick={() => handleTranslate(hotel.adminDescription?.sv || hotel.adminDescription?.da || '', hotel.adminDescription?.sv ? 'sv' : 'da', 'en', 'adminDescription.en')}
                                                disabled={translating === 'adminDescription.en' || (!hotel.adminDescription?.sv && !hotel.adminDescription?.da)}
                                                className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 disabled:opacity-40"
                                            >
                                                {translating === 'adminDescription.en' ? '...' : 'Translate'}
                                            </button>
                                        </div>
                                        <HtmlEditor
                                            value={hotel.adminDescription?.en || ''}
                                            onChange={html => setHotel({ ...hotel, adminDescription: { ...hotel.adminDescription, en: html } })}
                                            isDark={false}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">🇸🇪 Svenska</label>
                                            <button
                                                onClick={() => handleTranslate(hotel.adminDescription?.en || hotel.adminDescription?.da || '', hotel.adminDescription?.en ? 'en' : 'da', 'sv', 'adminDescription.sv')}
                                                disabled={translating === 'adminDescription.sv' || (!hotel.adminDescription?.en && !hotel.adminDescription?.da)}
                                                className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 disabled:opacity-40"
                                            >
                                                {translating === 'adminDescription.sv' ? '...' : 'Translate'}
                                            </button>
                                        </div>
                                        <HtmlEditor
                                            value={hotel.adminDescription?.sv || ''}
                                            onChange={html => setHotel({ ...hotel, adminDescription: { ...hotel.adminDescription, sv: html } })}
                                            isDark={false}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">🇩🇰 Dansk</label>
                                            <button
                                                onClick={() => handleTranslate(hotel.adminDescription?.en || hotel.adminDescription?.sv || '', hotel.adminDescription?.en ? 'en' : 'sv', 'da', 'adminDescription.da')}
                                                disabled={translating === 'adminDescription.da' || (!hotel.adminDescription?.en && !hotel.adminDescription?.sv)}
                                                className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 disabled:opacity-40"
                                            >
                                                {translating === 'adminDescription.da' ? '...' : 'Translate'}
                                            </button>
                                        </div>
                                        <HtmlEditor
                                            value={hotel.adminDescription?.da || ''}
                                            onChange={html => setHotel({ ...hotel, adminDescription: { ...hotel.adminDescription, da: html } })}
                                            isDark={false}
                                        />
                                    </div>
                                </div>

                                {/* Short description EN/SV/DA */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">🇬🇧 Short (EN)</label>
                                            <button
                                                onClick={() => handleTranslate(hotel.adminShortDescription?.sv || hotel.adminShortDescription?.da || '', hotel.adminShortDescription?.sv ? 'sv' : 'da', 'en', 'adminShortDescription.en')}
                                                disabled={translating === 'adminShortDescription.en' || (!hotel.adminShortDescription?.sv && !hotel.adminShortDescription?.da)}
                                                className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 disabled:opacity-40"
                                            >
                                                {translating === 'adminShortDescription.en' ? '...' : 'Translate'}
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            value={hotel.adminShortDescription?.en || ''}
                                            onChange={e => setHotel({ ...hotel, adminShortDescription: { ...hotel.adminShortDescription, en: e.target.value } })}
                                            placeholder="Short English summary..."
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">🇸🇪 Kort (SV)</label>
                                            <button
                                                onClick={() => handleTranslate(hotel.adminShortDescription?.en || hotel.adminShortDescription?.da || '', hotel.adminShortDescription?.en ? 'en' : 'da', 'sv', 'adminShortDescription.sv')}
                                                disabled={translating === 'adminShortDescription.sv' || (!hotel.adminShortDescription?.en && !hotel.adminShortDescription?.da)}
                                                className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 disabled:opacity-40"
                                            >
                                                {translating === 'adminShortDescription.sv' ? '...' : 'Translate'}
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            value={hotel.adminShortDescription?.sv || ''}
                                            onChange={e => setHotel({ ...hotel, adminShortDescription: { ...hotel.adminShortDescription, sv: e.target.value } })}
                                            placeholder="Kort sammanfattning..."
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">🇩🇰 Kort (DA)</label>
                                            <button
                                                onClick={() => handleTranslate(hotel.adminShortDescription?.en || hotel.adminShortDescription?.sv || '', hotel.adminShortDescription?.en ? 'en' : 'sv', 'da', 'adminShortDescription.da')}
                                                disabled={translating === 'adminShortDescription.da' || (!hotel.adminShortDescription?.en && !hotel.adminShortDescription?.sv)}
                                                className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 disabled:opacity-40"
                                            >
                                                {translating === 'adminShortDescription.da' ? '...' : 'Translate'}
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            value={hotel.adminShortDescription?.da || ''}
                                            onChange={e => setHotel({ ...hotel, adminShortDescription: { ...hotel.adminShortDescription, da: e.target.value } })}
                                            placeholder="Kort dansk resumé..."
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                                        />
                                    </div>
                                </div>

                                {hotel.adminDescription?.generatedAt && (
                                    <p className="text-[10px] text-gray-400">Last generated: {new Date(hotel.adminDescription.generatedAt).toLocaleString()} by {hotel.adminDescription.generatedBy || 'unknown'}</p>
                                )}
                            </div>

                            {/* Location */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
                                    <input
                                        type="text"
                                        value={hotel.location?.country || ''}
                                        onChange={e => setHotel({ ...hotel, location: { ...hotel.location, country: e.target.value } })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">City</label>
                                    <input
                                        type="text"
                                        value={hotel.location?.city || ''}
                                        onChange={e => setHotel({ ...hotel, location: { ...hotel.location, city: e.target.value } })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Address</label>
                                    <input
                                        type="text"
                                        value={hotel.location?.address || ''}
                                        onChange={e => setHotel({ ...hotel, location: { ...hotel.location, address: e.target.value } })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    />
                                </div>
                            </div>
                            
                            {/* Distances */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Distance to Beach (m)</label>
                                    <input
                                        type="number"
                                        value={hotel.distanceToBeach || ''}
                                        onChange={e => setHotel({ ...hotel, distanceToBeach: parseInt(e.target.value) || undefined })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        placeholder="e.g. 150"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Distance to Center (m)</label>
                                    <input
                                        type="number"
                                        value={hotel.distanceToCenter || ''}
                                        onChange={e => setHotel({ ...hotel, distanceToCenter: parseInt(e.target.value) || undefined })}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        placeholder="e.g. 2000"
                                    />
                                </div>
                            </div>

                            {/* Contact & Links */}
                            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-5 space-y-4">
                                <h4 className="font-semibold text-cyan-800 dark:text-cyan-300 flex items-center gap-2">📞 Contact & Links</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">📞 Phone</label>
                                        <input
                                            type="text"
                                            value={hotel.contact?.phone || ''}
                                            onChange={e => setHotel({ ...hotel, contact: { ...hotel.contact, phone: e.target.value } })}
                                            placeholder="+90 242 555 1234"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">📠 Fax</label>
                                        <input
                                            type="text"
                                            value={hotel.contact?.fax || ''}
                                            onChange={e => setHotel({ ...hotel, contact: { ...hotel.contact, fax: e.target.value } })}
                                            placeholder="+90 242 555 1235"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">✉️ Email</label>
                                        <input
                                            type="email"
                                            value={hotel.contact?.email || ''}
                                            onChange={e => setHotel({ ...hotel, contact: { ...hotel.contact, email: e.target.value } })}
                                            placeholder="info@hotel.com"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">🌐 Website (homePage)</label>
                                        <input
                                            type="url"
                                            value={hotel.contact?.website || ''}
                                            onChange={e => setHotel({ ...hotel, contact: { ...hotel.contact, website: e.target.value } })}
                                            placeholder="https://www.hotel.com"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        />
                                        {hotel.contact?.website && (
                                            <a href={hotel.contact.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline mt-0.5 inline-block">↗ Open website</a>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">📸 Instagram URL</label>
                                        <input
                                            type="url"
                                            value={hotel.instagramUrl || ''}
                                            onChange={e => setHotel({ ...hotel, instagramUrl: e.target.value })}
                                            placeholder="https://instagram.com/hotel"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">🔗 Web URL (Custom)</label>
                                        <input
                                            type="url"
                                            value={hotel.webUrl || ''}
                                            onChange={e => setHotel({ ...hotel, webUrl: e.target.value })}
                                            placeholder="https://lumitours.se/hotels/..."
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Themes & Concepts */}
                            {((hotel.themes && hotel.themes.length > 0) || (hotel.concepts && hotel.concepts.length > 0)) && (
                                <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 space-y-3">
                                    <h4 className="font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">🏷️ Themes & Concepts <span className="text-xs font-normal text-amber-500">(from Tourvisio API)</span></h4>
                                    {hotel.themes && hotel.themes.length > 0 && (
                                        <div>
                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Themes:</span>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {hotel.themes.map((theme, idx) => (
                                                    <span key={idx} className="px-3 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full text-xs font-medium">
                                                        {theme.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {hotel.concepts && hotel.concepts.length > 0 && (
                                        <div>
                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Concepts:</span>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {hotel.concepts.map((concept, idx) => (
                                                    <span key={idx} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium" title={concept.description || ''}>
                                                        {concept.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Tourvisio API Texts (DETAIL1, SEARCH, etc.) */}
                            {hotel.tourvisioTexts && hotel.tourvisioTexts.length > 0 && (
                                <div className="bg-gradient-to-r from-sky-50 to-cyan-50 dark:from-sky-900/20 dark:to-cyan-900/20 rounded-xl p-5 space-y-3">
                                    <h4 className="font-semibold text-sky-800 dark:text-sky-300 flex items-center gap-2">
                                        📄 Tourvisio API Texts <span className="text-xs font-normal text-sky-500">({hotel.tourvisioTexts.length} categories)</span>
                                    </h4>
                                    <div className="space-y-3">
                                        {hotel.tourvisioTexts.map((textCat, idx) => (
                                            <div key={idx} className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="px-2 py-0.5 bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 rounded text-[10px] font-bold uppercase">{textCat.code}</span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">{textCat.name}</span>
                                                </div>
                                                <div
                                                    className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed max-h-32 overflow-y-auto prose prose-xs dark:prose-invert"
                                                    dangerouslySetInnerHTML={{ __html: textCat.text || '' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Facilities - Categorized */}
                            {hotel.facilityCategories && hotel.facilityCategories.length > 0 ? (
                                <div className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl p-5 space-y-3">
                                    <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                                        🏊 Facilities by Category <span className="text-xs font-normal text-emerald-500">({hotel.facilities?.length || 0} total)</span>
                                    </h4>
                                    <div className="space-y-3">
                                        {hotel.facilityCategories.map((cat, catIdx) => (
                                            <div key={catIdx} className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3">
                                                <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1">
                                                    📁 {cat.name} <span className="text-[10px] font-normal text-gray-400">({cat.facilities.length})</span>
                                                </div>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {cat.facilities.map((facility, fIdx) => (
                                                        <span key={fIdx} className={`px-2 py-0.5 rounded text-[11px] font-medium ${facility.isPriced
                                                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                                                            : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                                            }`} title={facility.note || ''}>
                                                            {facility.name}{facility.isPriced ? ' 💰' : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : hotel.facilities && hotel.facilities.length > 0 && (
                                <div className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl p-5 space-y-3">
                                    <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                                        🏊 Facilities <span className="text-xs font-normal text-emerald-500">({hotel.facilities.length} total)</span>
                                    </h4>
                                    <div className="flex flex-wrap gap-1.5">
                                        {hotel.facilities.map((facility, idx) => (
                                            <span key={idx} className={`px-2 py-0.5 rounded text-[11px] font-medium ${facility.isPaid
                                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                                                : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                                }`} title={facility.category ? `Category: ${facility.category}` : ''}>
                                                {facility.name}{facility.isPaid ? ' 💰' : ''}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Status flags */}
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={hotel.isActive}
                                        onChange={e => setHotel({ ...hotel, isActive: e.target.checked })}
                                        className="w-5 h-5 rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={hotel.isFeatured}
                                        onChange={e => setHotel({ ...hotel, isFeatured: e.target.checked })}
                                        className="w-5 h-5 rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Featured</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={hotel.lockContent}
                                        onChange={e => setHotel({ ...hotel, lockContent: e.target.checked })}
                                        className="w-5 h-5 rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">🔒 Lock Content</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Gallery Tab */}
                    {activeTab === 'gallery' && (
                        <div className="space-y-6">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <label className="flex-1 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-500 transition-colors bg-gray-50 dark:bg-gray-800/50">
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={e => e.target.files && handleImageUpload(e.target.files)}
                                            className="hidden"
                                            disabled={uploading}
                                        />
                                        <div className="text-4xl mb-2">📤</div>
                                        <p className="text-gray-600 dark:text-gray-400 font-medium">
                                            {uploading ? 'Uploading...' : 'Drop images or click to upload'}
                                        </p>
                                    </label>

                                    <div className="flex flex-col gap-3 w-full sm:w-1/3">
                                        <button
                                            onClick={handleFetchApiImages}
                                            disabled={saving}
                                            className="flex-1 px-6 py-4 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-xl hover:bg-indigo-200 transition-colors flex flex-col items-center justify-center"
                                        >
                                            <div className="text-2xl mb-1">🌐</div>
                                            <p className="text-sm font-bold">Sync from API</p>
                                        </button>

                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                placeholder="Image URL..."
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                                value={imageUrlInput}
                                                onChange={(e) => setImageUrlInput(e.target.value)}
                                            />
                                            <button
                                                onClick={() => setHotelImagePickerOpen(true)}
                                                className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 flex items-center gap-1"
                                            >
                                                <span className="material-icons text-sm">folder_open</span>
                                                Browse
                                            </button>
                                            <button
                                                onClick={handleAddImageUrl}
                                                disabled={!imageUrlInput}
                                                className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-between items-center px-2">
                                    <h4 className="font-bold text-gray-400 uppercase text-xs tracking-wider">{hotel.images?.length || 0} Images</h4>
                                    <span className="text-xs text-gray-400">Drag to reorder (coming soon)</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {hotel.images?.map((img, idx) => (
                                    <div key={idx} className={`relative group rounded-xl overflow-hidden aspect-video bg-gray-100 dark:bg-gray-800 border-2 transition-all ${img.isMain ? 'border-yellow-400 shadow-lg scale-[1.02]' : 'border-transparent hover:border-blue-300'}`}>
                                        <img
                                            src={getThumbnailUrl(img.url, 300) || ''}
                                            alt={`Hotel ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />

                                        {/* Overlay Controls */}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-4">
                                            {!img.isMain && (
                                                <button
                                                    onClick={() => handleSetMainImage(idx)}
                                                    className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-bold rounded-lg w-full transform transition-transform hover:scale-105"
                                                >
                                                    ★ Set as Main
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDeleteImage(idx)}
                                                className="px-3 py-1.5 bg-red-600/80 hover:bg-red-600 text-white text-xs font-bold rounded-lg w-full"
                                            >
                                                🗑️ Delete
                                            </button>
                                            <div className="text-[10px] text-gray-300 mt-2 truncate max-w-full">
                                                {img.source || 'unknown'}
                                            </div>
                                        </div>

                                        {img.isMain && (
                                            <div className="absolute top-2 left-2 bg-yellow-400 text-black text-[10px] px-2 py-0.5 rounded shadow-sm font-black uppercase tracking-wide">
                                                Main Image
                                            </div>
                                        )}
                                        {img.source === 'api' && (
                                            <div className="absolute top-2 right-2 bg-blue-500/80 text-white text-[10px] px-2 py-0.5 rounded shadow-sm font-bold">
                                                API
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {(!hotel.images || hotel.images.length === 0) && (
                                    <div className="col-span-4 py-12 text-center text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                                        <p className="mb-2 text-4xl">🖼️</p>
                                        <p>No images yet.</p>
                                        <p className="text-sm opacity-70">Upload files or import from API.</p>
                                    </div>
                                )}
                            </div>

                            {/* Google Photos Section */}
                            <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="flex items-center gap-4">
                                        <h4 className="font-bold text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider flex items-center gap-2">
                                            <span className="text-lg">🗺️</span> Google Maps Photos
                                        </h4>
                                        <span className="text-xs text-gray-400">
                                            {googleImages.length > 0 ? `(${googleImages.length} found)` : ''}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={approvedGoogleImages}
                                                onChange={handleToggleGoogleApproval}
                                                className="w-4 h-4 rounded border-gray-300"
                                            />
                                            <span className="text-gray-600 dark:text-gray-400">Show on Website</span>
                                        </label>
                                        <button
                                            onClick={handleFetchGoogleImages}
                                            disabled={loadingGoogleImages}
                                            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                                        >
                                            {loadingGoogleImages ? (
                                                <>
                                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Loading...
                                                </>
                                            ) : (
                                                <>🗺️ Fetch Google Photos</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {googleImages.length > 0 ? (
                                    <>
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                            {(showAllGoogleImages ? googleImages : googleImages.slice(0, GOOGLE_PHOTOS_INITIAL_LIMIT)).map((img, idx) => (
                                                <div
                                                    key={idx}
                                                    className="relative group rounded-lg overflow-hidden aspect-square bg-gray-100 dark:bg-gray-800 border-2 border-transparent hover:border-orange-400 cursor-pointer transition-all"
                                                    onClick={() => handleApproveGoogleImages([img.url])}
                                                >
                                                    <img
                                                        src={img.thumbnail || img.url}
                                                        alt={`Google Photo ${idx + 1}`}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder-hotel.jpg'; }}
                                                    />
                                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <span className="text-white text-xs font-bold bg-green-600 px-3 py-1 rounded-full">
                                                            ✓ Add to Gallery
                                                        </span>
                                                    </div>
                                                    <div className="absolute bottom-1 right-1 bg-orange-500/80 text-white text-[9px] px-1.5 py-0.5 rounded">
                                                        Google
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Load More Button */}
                                        {googleImages.length > GOOGLE_PHOTOS_INITIAL_LIMIT && !showAllGoogleImages && (
                                            <div className="mt-4 text-center">
                                                <button
                                                    onClick={() => setShowAllGoogleImages(true)}
                                                    className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                                                >
                                                    📷 Load More ({googleImages.length - GOOGLE_PHOTOS_INITIAL_LIMIT} more photos)
                                                </button>
                                            </div>
                                        )}

                                        {/* Add All Button */}
                                        <div className="mt-4 flex justify-end gap-3">
                                            <button
                                                onClick={() => handleApproveGoogleImages(googleImages.map(g => g.url))}
                                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
                                            >
                                                ✓ Add All {googleImages.length} Photos to Gallery
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="py-8 text-center text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/30">
                                        <p className="text-3xl mb-2">🗺️</p>
                                        <p>No Google photos loaded yet.</p>
                                        <p className="text-sm opacity-70 mt-1">Click "Fetch Google Photos" to search.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Room Types Tab */}
                    {activeTab === 'rooms' && (
                        <div className="bg-slate-800 rounded-xl">
                            <RoomMappingPanel hotelId={hotelId} hotelName={hotel.name} />
                        </div>
                    )}

                    {/* Cached Data Tab */}
                    {activeTab === 'cached' && (
                        <div className="space-y-6">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                <h3 className="text-lg font-semibold dark:text-white">Cached Data</h3>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={handleSyncCache}
                                        disabled={saving}
                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
                                    >
                                        <span>🔄</span> Sync from Cache
                                    </button>
                                    <button
                                        onClick={async () => {
                                            setSyncingTourvisio(true);
                                            try {
                                                const res = await fetch(`${API_BASE}/${hotelId}/sync-tourvisio`, { method: 'POST' });
                                                const data = await res.json();
                                                if (data.success) {
                                                    setMessage({ type: 'success', text: data.message || 'Synced from Tourvisio' });
                                                    if (data.hotel) setHotel(data.hotel);
                                                    fetchCachedData();
                                                } else {
                                                    setMessage({ type: 'error', text: data.error || 'Tourvisio sync failed' });
                                                }
                                            } catch (error) {
                                                setMessage({ type: 'error', text: 'Tourvisio sync failed' });
                                            } finally {
                                                setSyncingTourvisio(false);
                                            }
                                        }}
                                        disabled={syncingTourvisio || !hotel.tourvisioId}
                                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 transition-colors"
                                    >
                                        {syncingTourvisio ? '⏳ Syncing...' : '🌐 Tourvisio Sync'}
                                    </button>
                                </div>
                            </div>

                            {/* Full Cache Manager with prices table, filters, search history, and fetch */}
                            <HotelCacheManager hotelId={hotelId} hotelName={hotel.name} />
                        </div>
                    )}

                    {/* Reviews Tab */}
                    {activeTab === 'reviews' && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold dark:text-white">Booking Reviews</h3>
                                <button
                                    onClick={handleFetchReviews}
                                    disabled={fetchingReviews}
                                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                >
                                    {fetchingReviews ? (
                                        <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Fetching...</>
                                    ) : '🤖 Fetch Reviews with AI'}
                                </button>
                            </div>

                            {/* Score & Count */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-xl p-5 text-center">
                                    <div className="text-4xl font-bold text-amber-600">{hotel.bookingReviews?.score?.toFixed(1) || '—'}</div>
                                    <div className="text-sm text-gray-500 mt-1">Score</div>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="10"
                                        value={hotel.bookingReviews?.score || ''}
                                        onChange={e => setHotel({ ...hotel, bookingReviews: { ...hotel.bookingReviews, score: parseFloat(e.target.value) || 0 } })}
                                        className="mt-2 w-20 mx-auto px-2 py-1 text-center text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600"
                                    />
                                </div>
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 text-center">
                                    <div className="text-4xl font-bold text-blue-600">{hotel.bookingReviews?.reviewCount?.toLocaleString() || '—'}</div>
                                    <div className="text-sm text-gray-500 mt-1">Reviews</div>
                                    <input
                                        type="number"
                                        value={hotel.bookingReviews?.reviewCount || ''}
                                        onChange={e => setHotel({ ...hotel, bookingReviews: { ...hotel.bookingReviews, reviewCount: parseInt(e.target.value) || 0 } })}
                                        className="mt-2 w-24 mx-auto px-2 py-1 text-center text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600"
                                    />
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 text-center">
                                    <div className="text-2xl font-bold text-green-600">{hotel.bookingReviews?.guestMood?.en || '—'}</div>
                                    <div className="text-sm text-gray-500 mt-1">Guest Mood</div>
                                    <div className="text-xs text-gray-400">{hotel.bookingReviews?.guestMood?.sv || ''}</div>
                                </div>
                            </div>

                            {/* Summary */}
                            {hotel.bookingReviews?.summary && (
                                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                                    <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">Summary</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <span className="text-[10px] text-gray-400">🇬🇧 EN</span>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{hotel.bookingReviews.summary.en || 'No summary'}</p>
                                        </div>
                                        <div>
                                            <span className="text-[10px] text-gray-400">🇸🇪 SV</span>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{hotel.bookingReviews.summary.sv || 'No summary'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Pros */}
                            {hotel.bookingReviews?.pros && hotel.bookingReviews.pros.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">✅ Pros</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {hotel.bookingReviews.pros.map((pro, idx) => (
                                            <span key={idx} className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs">
                                                {pro.en || pro.sv || 'N/A'}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Individual Reviews */}
                            <div>
                                <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-3">Individual Reviews ({hotel.bookingReviews?.reviews?.length || 0})</h4>
                                <div className="space-y-3">
                                    {hotel.bookingReviews?.reviews?.map((review, idx) => (
                                        <div key={idx} className={`border rounded-xl p-4 transition-all ${review.isVisible !== false
                                            ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                            : 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/10 opacity-60'
                                            }`}>
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/50 rounded-full flex items-center justify-center text-sm font-bold text-indigo-600">
                                                        {(review.author || 'G')[0]}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-sm">{review.author || 'Guest'}</span>
                                                        <div className="flex items-center gap-2 text-xs text-gray-400">
                                                            <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded font-bold">
                                                                {review.rating?.toFixed(1) || '—'}
                                                            </span>
                                                            <span>{review.source || 'ai'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        if (!hotel.bookingReviews?.reviews) return;
                                                        const updated = [...hotel.bookingReviews.reviews];
                                                        updated[idx] = { ...updated[idx], isVisible: !(updated[idx].isVisible !== false) };
                                                        setHotel({ ...hotel, bookingReviews: { ...hotel.bookingReviews, reviews: updated } });
                                                    }}
                                                    className={`text-xs px-2 py-1 rounded ${review.isVisible !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                                                >
                                                    {review.isVisible !== false ? '👁 Visible' : '🚫 Hidden'}
                                                </button>
                                            </div>
                                            <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <span className="text-[10px] text-gray-400">🇬🇧</span>
                                                    <p className="text-gray-600 dark:text-gray-400">{review.text?.en || ''}</p>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] text-gray-400">🇸🇪</span>
                                                    <p className="text-gray-600 dark:text-gray-400">{review.text?.sv || ''}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )) || (
                                            <div className="py-8 text-center text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                                                <p className="text-3xl mb-2">⭐</p>
                                                <p>No reviews yet.</p>
                                                <p className="text-sm opacity-70 mt-1">Click "Fetch Reviews with AI" to generate.</p>
                                            </div>
                                        )}
                                </div>
                            </div>

                            {hotel.bookingReviews?.lastFetched && (
                                <p className="text-xs text-gray-400">Last fetched: {new Date(hotel.bookingReviews.lastFetched).toLocaleString()}</p>
                            )}
                        </div>
                    )}

                    {/* Features Tab */}
                    {activeTab === 'features' && (
                        <div className="space-y-6">
                            {/* Facilities */}
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                                <h4 className="font-semibold mb-4 text-emerald-700 dark:text-emerald-400">🏊 Features & Facilities</h4>
                                {hotel.facilities && hotel.facilities.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {hotel.facilities.map(facility => (
                                            <span key={facility.id} className={`px-2 py-1 text-xs rounded-full border ${facility.isPaid ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:border-amber-800' : 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:border-green-800'}`}>
                                                {facility.name} {facility.isPaid ? '($)' : ''}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">No facilities found. Click "Sync GetProductInfo" in API Sync tab.</p>
                                )}
                            </div>

                            {/* Themes & Concepts */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                                    <h4 className="font-semibold mb-4 text-amber-700 dark:text-amber-400">🏷️ Themes</h4>
                                    {hotel.themes && hotel.themes.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {hotel.themes.map(theme => (
                                                <span key={theme.id} className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800">
                                                    {theme.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500">No themes found.</p>
                                    )}
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                                    <h4 className="font-semibold mb-4 text-purple-700 dark:text-purple-400">⚡ Concepts</h4>
                                    {hotel.concepts && hotel.concepts.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {hotel.concepts.map(concept => (
                                                <span key={concept.id} className="px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800" title={concept.description}>
                                                    {concept.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500">No concepts found.</p>
                                    )}
                                </div>
                            </div>

                            {/* Tourvisio Texts */}
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                                <h4 className="font-semibold mb-4 text-sky-700 dark:text-sky-400">📄 Tourvisio Texts (Raw)</h4>
                                {hotel.tourvisioTexts && hotel.tourvisioTexts.length > 0 ? (
                                    <div className="space-y-4">
                                        {hotel.tourvisioTexts.map((textObj, idx) => (
                                            <div key={idx} className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                                <h5 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                                                    {textObj.name} <span className="text-xs text-gray-400 font-normal ml-2">({textObj.code})</span>
                                                </h5>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{textObj.text}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">No raw text categories found from Tourvisio.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* API Sync Tab */}
                    {/* Media Tab */}
                    {activeTab === 'media' && (
                        <div className="space-y-6">
                            <HotelVideoManager hotelId={hotelId} />
                        </div>
                    )}

                    {/* Page Builder Tab */}
                    {activeTab === 'page_builder' && (
                        <div className="space-y-6">
                            <HotelPageBuilder hotelId={hotelId} />
                        </div>
                    )}

                    {/* Enriched Content Tab */}
                    {activeTab === 'enriched' && (
                        <div className="space-y-6">
                            <HotelEnrichment
                                hotelCode={hotel.tourvisioId}
                                hotelName={hotel.name}
                                hotelWebsite={hotel.websiteUrl}
                                autoLoad={true}
                            />
                        </div>
                    )}

                    {activeTab === 'api' && (
                        <div className="space-y-6">
                            {/* Tourvisio GetProductInfo Sync */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="font-semibold mb-1 dark:text-white">🔄 Tourvisio GetProductInfo</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                            Sync hotel details from Tourvisio API: contact info, themes, facilities, room types, descriptions.
                                        </p>
                                    </div>
                                    {hotel.lastApiSync && (
                                        <div className="text-right text-xs text-gray-400">
                                            <div>Last sync: {new Date(hotel.lastApiSync).toLocaleString()}</div>
                                            {hotel.apiSyncSource && <div className="text-[10px]">via {hotel.apiSyncSource}</div>}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={async () => {
                                            setSyncingTourvisio(true);
                                            setSyncResult(null);
                                            try {
                                                const res = await fetch(`${API_BASE}/${hotelId}/sync-tourvisio`, { method: 'POST' });
                                                const data = await res.json();
                                                if (data.success) {
                                                    setMessage({ type: 'success', text: data.message || 'Synced from Tourvisio' });
                                                    setSyncResult(data.syncSummary);
                                                    if (data.hotel) setHotel(data.hotel);
                                                    fetchCachedData();
                                                } else {
                                                    setMessage({ type: 'error', text: data.error || 'Sync failed' });
                                                }
                                            } catch (error) {
                                                setMessage({ type: 'error', text: 'Tourvisio sync failed' });
                                            } finally {
                                                setSyncingTourvisio(false);
                                            }
                                        }}
                                        disabled={syncingTourvisio || !hotel.tourvisioId}
                                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 transition-all flex items-center gap-2"
                                    >
                                        {syncingTourvisio ? (
                                            <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Syncing...</>
                                        ) : '🔄 Sync GetProductInfo'}
                                    </button>
                                    <button
                                        onClick={async () => {
                                            setSaving(true);
                                            try {
                                                const res = await fetch(`${API_BASE}/${hotelId}/sync-cache`, { method: 'POST' });
                                                const data = await res.json();
                                                if (data.success) {
                                                    const msg = data.message || 'Synced from API';
                                                    const priceInfo = data.priceData
                                                        ? ` | Price: ${data.priceData.currency} ${data.priceData.min}-${data.priceData.max}`
                                                        : '';
                                                    setMessage({ type: 'success', text: msg + priceInfo });
                                                    fetchHotel();
                                                    fetchCachedData();
                                                } else {
                                                    setMessage({ type: 'error', text: data.error || 'No data found' });
                                                }
                                            } catch (error) {
                                                setMessage({ type: 'error', text: 'Cache sync failed' });
                                            } finally {
                                                setSaving(false);
                                            }
                                        }}
                                        disabled={saving}
                                        className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium disabled:opacity-50 transition-colors"
                                    >
                                        {saving ? 'Syncing...' : '💾 Sync Cache & Prices'}
                                    </button>
                                </div>
                                {!hotel.tourvisioId && (
                                    <p className="mt-2 text-xs text-red-500">⚠️ No Tourvisio ID set — sync is disabled</p>
                                )}
                            </div>

                            {/* Sync Result Summary */}
                            {syncResult && (
                                <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-800 rounded-xl p-5">
                                    <h4 className="font-semibold mb-3 text-green-800 dark:text-green-300">✅ Sync Result</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        {syncResult.homePage && (
                                            <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                                                <div className="text-[10px] text-gray-500">🌐 Website</div>
                                                <div className="font-medium text-blue-700 dark:text-blue-400 truncate text-xs">{syncResult.homePage}</div>
                                            </div>
                                        )}
                                        {syncResult.phone && (
                                            <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                                                <div className="text-[10px] text-gray-500">📞 Phone</div>
                                                <div className="font-medium text-xs">{syncResult.phone}</div>
                                            </div>
                                        )}
                                        {syncResult.faxNumber && (
                                            <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                                                <div className="text-[10px] text-gray-500">📠 Fax</div>
                                                <div className="font-medium text-xs">{syncResult.faxNumber}</div>
                                            </div>
                                        )}
                                        <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg">
                                            <div className="text-[10px] text-gray-500">🏷️ Themes</div>
                                            <div className="font-bold text-lg text-amber-700 dark:text-amber-400">{syncResult.themesCount}</div>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-lg">
                                            <div className="text-[10px] text-gray-500">🏊 Facilities</div>
                                            <div className="font-bold text-lg text-emerald-700 dark:text-emerald-400">{syncResult.facilitiesCount}</div>
                                        </div>
                                        <div className="bg-sky-50 dark:bg-sky-900/20 p-2 rounded-lg">
                                            <div className="text-[10px] text-gray-500">📄 Text Categories</div>
                                            <div className="font-bold text-lg text-sky-700 dark:text-sky-400">{syncResult.textCategoriesCount || 0}</div>
                                        </div>
                                        <div className="bg-teal-50 dark:bg-teal-900/20 p-2 rounded-lg">
                                            <div className="text-[10px] text-gray-500">📁 Facility Groups</div>
                                            <div className="font-bold text-lg text-teal-700 dark:text-teal-400">{syncResult.facilityCategoriesCount || 0}</div>
                                        </div>
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded-lg">
                                            <div className="text-[10px] text-gray-500">🛏️ Room Types</div>
                                            <div className="font-bold text-lg text-purple-700 dark:text-purple-400">{syncResult.roomTypesCount}</div>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded-lg">
                                            <div className="text-[10px] text-gray-500">🍽️ Board Types</div>
                                            <div className="font-bold text-lg text-indigo-700 dark:text-indigo-400">{syncResult.boardTypesCount}</div>
                                        </div>
                                        <div className="bg-pink-50 dark:bg-pink-900/20 p-2 rounded-lg">
                                            <div className="text-[10px] text-gray-500">🖼️ Media Files</div>
                                            <div className="font-bold text-lg text-pink-700 dark:text-pink-400">{syncResult.mediaFilesCount}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Display current price range if available */}
                            {hotel.priceRange && (
                                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6">
                                    <h4 className="font-semibold mb-2 dark:text-white">💰 Current Pricing</h4>
                                    <div className="flex gap-6 text-sm">
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Min Price:</span>
                                            <span className="ml-2 font-semibold text-green-700 dark:text-green-400">
                                                {hotel.priceRange.currency} {hotel.priceRange.min}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Max Price:</span>
                                            <span className="ml-2 font-semibold text-green-700 dark:text-green-400">
                                                {hotel.priceRange.currency} {hotel.priceRange.max}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Booking.com Reviews Sync */}
                            <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-6">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="font-semibold mb-1 dark:text-white">🏨 Booking.com Reviews & Rating</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                            Fetch hotel reviews, scores, and guest sentiment via AI. Data is saved to the Reviews tab.
                                        </p>
                                    </div>
                                    {hotel.bookingReviews?.lastFetched && (
                                        <div className="text-right text-xs text-gray-400">
                                            <div>Last sync: {new Date(hotel.bookingReviews.lastFetched).toLocaleString()}</div>
                                            <div className="text-[10px]">
                                                Score: {hotel.bookingReviews?.score?.toFixed(1) || '—'} ({hotel.bookingReviews?.reviewCount || 0} reviews)
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-3 items-center">
                                    <button
                                        onClick={handleFetchReviews}
                                        disabled={fetchingReviews}
                                        className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-lg font-medium disabled:opacity-50 transition-all flex items-center gap-2"
                                    >
                                        {fetchingReviews ? (
                                            <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Fetching...</>
                                        ) : '🤖 Fetch Reviews with AI'}
                                    </button>
                                    {hotel.bookingReviews?.score && (
                                        <span className="text-sm">
                                            <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg font-bold">
                                                {hotel.bookingReviews.score.toFixed(1)}/10
                                            </span>
                                            <span className="ml-1 text-gray-400">({hotel.bookingReviews.reviewCount || 0})</span>
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Instagram Sync */}
                            <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h4 className="font-semibold mb-1 dark:text-white">📸 Instagram</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Hotel Instagram URL — synced from allhotels.xlsx or manually edited.
                                        </p>
                                    </div>
                                    {hotel.instagramUrl && (
                                        <a
                                            href={hotel.instagramUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-pink-600 hover:text-pink-700 flex items-center gap-1"
                                        >
                                            🔗 Open
                                        </a>
                                    )}
                                </div>
                                <div className="flex gap-3 items-center">
                                    <input
                                        type="text"
                                        value={hotel.instagramUrl || ''}
                                        onChange={e => setHotel({ ...hotel, instagramUrl: e.target.value })}
                                        placeholder="https://www.instagram.com/hotelname"
                                        className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    />
                                    {hotel.instagramUrl && (
                                        <button
                                            onClick={() => setHotel({ ...hotel, instagramUrl: '' })}
                                            className="px-3 py-2.5 text-gray-400 hover:text-red-500 transition-colors"
                                            title="Clear Instagram URL"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="border-t dark:border-gray-700 pt-6">
                                <h4 className="font-semibold mb-4 dark:text-white">Hotel IDs</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                                        <span className="text-gray-500">Database ID:</span>
                                        <span className="ml-2 font-mono">{hotel._id}</span>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                                        <span className="text-gray-500">Tourvisio ID:</span>
                                        <span className="ml-2 font-mono">{hotel.tourvisioId}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4 flex flex-wrap gap-y-3 justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex gap-2 sm:gap-3 flex-wrap">
                        <button
                            onClick={onClose}
                            className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 hover:text-gray-900"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={deleting}
                            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-1.5 sm:gap-2"
                        >
                            {deleting ? (
                                <>
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Deleting...
                                </>
                            ) : (
                                <>🗑️ Delete Hotel</>
                            )}
                        </button>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 sm:px-6 py-2 text-sm sm:text-base bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {/* Image Picker Modal */}
            <ImagePickerModal
                isOpen={hotelImagePickerOpen}
                onClose={() => setHotelImagePickerOpen(false)}
                onSelect={(url) => {
                    setImageUrlInput(url);
                    setHotelImagePickerOpen(false);
                }}
                title="Select Hotel Image"
            />
        </div>
    );
};

export default HotelDetailEditor;
